import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Section from '../components/Section';
import WeekTabs from '../components/WeekTabs';
import HeaderBar from '../components/HeaderBar';
import type { DiaryItem } from '../models/diary';
import DiaryItemCard from '../components/DiaryItemCard';
import AdherenceChart from '../components/AdherenceChart';
import AdherenceHeatmap from '../components/AdherenceHeatmap';
import { adherenceService, AdherenceStatistic } from '../services/adherenceService';
import Tabs from '../components/Tabs';
import EditPatientModal from '../components/EditPatientModal';
import DiaryItemCreateModal from '../components/DiaryItemCreateModal';
import DocumentManager from '../components/DocumentManager';
import ConfirmModal from '../components/ConfirmModal';
import { patientsService, Patient, UpdatePatientData, getPatientActivityStatus } from '../services/patientsService';
import { diaryService } from '../services/diaryService';
import { remindersService } from '../services/remindersService';
import { Reminder, CreateSystemReminderData, UpdateReminderData } from '../models/reminder';
import ReminderCard from '../components/ReminderCard';
import ReminderModal from '../components/ReminderModal';





const PatientDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState(1); // Default to Martes
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0); // 0: Statistics, 1: Plan, 2: Other
  const [planItems, setPlanItems] = useState<DiaryItem[]>([]);
  const [diaryLoading, setDiaryLoading] = useState(false);
  const [diaryError, setDiaryError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DiaryItem | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalItemType, setCreateModalItemType] = useState<'FitnessItem' | 'SupplementItem'>('FitnessItem');
  const [adherenceData, setAdherenceData] = useState<AdherenceStatistic[]>([]);
  const [adherenceLoading, setAdherenceLoading] = useState(false);
  const [adherenceError, setAdherenceError] = useState<string | null>(null);
  const [heatmapData, setHeatmapData] = useState<AdherenceStatistic[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [remindersError, setRemindersError] = useState<string | null>(null);
  const [createReminderModalOpen, setCreateReminderModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [editReminderModalOpen, setEditReminderModalOpen] = useState(false);

  // Map selectedDay to day names for filtering diary items
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday','Sunday'];
  const selectedDayName = dayNames[selectedDay];
  
  // Filter diary items by selected day and sort by timeOfDay
  const filteredPlanItems = planItems
    .filter(item => 
      item.dayOfWeek === selectedDayName || 
      (item.dayOfWeek && item.dayOfWeek.toLowerCase() === selectedDayName.toLowerCase())
    )
    .sort((a, b) => (a.timeOfDay || '').localeCompare(b.timeOfDay || ''));

  // Fetch patient data when component mounts or ID changes
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const patientData = await patientsService.getPatientById(id);
        setPatient(patientData);
      } catch (err) {
        console.error('Error fetching patient:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  // Fetch diary items when patient ID changes
  useEffect(() => {
    const fetchDiaryItems = async () => {
      if (!id) return;

      try {
        setDiaryLoading(true);
        setDiaryError(null);
        const diaryItems = await diaryService.getAllDiaryItemsByUserId(id);
        // Sort items by timeOfDay when initially loading
        const sortedItems = diaryItems.sort((a, b) => (a.timeOfDay || '').localeCompare(b.timeOfDay || ''));
        setPlanItems(sortedItems);
      } catch (err) {
        setDiaryError('Failed to load diary items');
        console.error('Error fetching diary items:', err);
        setPlanItems([]);
      } finally {
        setDiaryLoading(false);
      }
    };

    fetchDiaryItems();
  }, [id]);

  // Fetch adherence data (line chart — last 10 weeks)
  useEffect(() => {
    const fetchAdherenceData = async () => {
      if (!patient?.userId) return;

      try {
        setAdherenceLoading(true);
        setAdherenceError(null);
        const adherenceStats = await adherenceService.getLastTenWeeksAdherence(patient.userId);
        setAdherenceData(adherenceStats);
      } catch (err) {
        setAdherenceError('Failed to load adherence data');
        console.error('Error fetching adherence data:', err);
        setAdherenceData([]);
      } finally {
        setAdherenceLoading(false);
      }
    };

    fetchAdherenceData();
  }, [patient?.userId]);

  // Fetch adherence data (heatmap — last 52 weeks)
  useEffect(() => {
    const fetchHeatmapData = async () => {
      if (!patient?.userId) return;

      try {
        setHeatmapLoading(true);
        setHeatmapError(null);
        const stats = await adherenceService.getYearAdherence(patient.userId);
        setHeatmapData(stats);
      } catch (err) {
        setHeatmapError('Failed to load heatmap data');
        console.error('Error fetching heatmap data:', err);
        setHeatmapData([]);
      } finally {
        setHeatmapLoading(false);
      }
    };

    fetchHeatmapData();
  }, [patient?.userId]);


  // Fetch reminders when patient is loaded
  useEffect(() => {
    const fetchReminders = async () => {
      if (!id) return;

      try {
        setRemindersLoading(true);
        setRemindersError(null);
        const data = await remindersService.getReminders(id);
        setReminders(data);
      } catch (err) {
        setRemindersError('Error al cargar los recordatorios');
        console.error('Error fetching reminders:', err);
        setReminders([]);
      } finally {
        setRemindersLoading(false);
      }
    };

    fetchReminders();
  }, [id]);

  // Reminder handlers
  const handleCreateReminder = async (data: CreateSystemReminderData | UpdateReminderData) => {
    if (!id) return;
    await remindersService.createReminder(id, data as CreateSystemReminderData);
    const updated = await remindersService.getReminders(id);
    setReminders(updated);
    setCreateReminderModalOpen(false);
  };

  const handleUpdateReminder = async (data: UpdateReminderData) => {
    if (!id || !editingReminder) return;
    await remindersService.updateReminder(id, editingReminder.id, data);
    const updated = await remindersService.getReminders(id);
    setReminders(updated);
    setEditReminderModalOpen(false);
    setEditingReminder(null);
  };

  const handleDeleteReminder = async (reminder: Reminder) => {
    if (!id) return;
    try {
      await remindersService.deleteReminder(id, reminder.id);
      setReminders(prev => prev.filter(r => r.id !== reminder.id));
    } catch (err) {
      console.error('Error deleting reminder:', err);
      alert('Error al eliminar el recordatorio. Por favor, inténtalo de nuevo.');
    }
  };

  const handleToggleReminderEnabled = async (reminder: Reminder) => {
    if (!id) return;
    const updated = await remindersService.updateReminder(id, reminder.id, { enabled: !reminder.enabled });
    setReminders(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  // Open modal for editing
  const handleEditItem = (item: DiaryItem) => {
    setEditingItem({ ...item });
    setEditModalOpen(true);
  };
  
  // Save changes (receives updated data from the unified modal)
  const handleSaveEdit = async (updatedItemData: Partial<DiaryItem>) => {
    if (!editingItem || !id) return;
    
    try {
      // Merge the updated data with the existing item to create a complete DiaryItem
      const completeItem: DiaryItem = {
        ...editingItem,
        ...updatedItemData,
        id: editingItem.id // Ensure ID is preserved
      };
      
      // Call the API to update the diary item with the merged data
      await diaryService.updateDiaryItem(id, editingItem.id, completeItem);
      
      // Refetch all diary items to get the latest data from the server
      const diaryItems = await diaryService.getAllDiaryItemsByUserId(id);
      const sortedItems = diaryItems.sort((a, b) => (a.timeOfDay || '').localeCompare(b.timeOfDay || ''));
      setPlanItems(sortedItems);
      
      setEditModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating diary item:', error);
      alert('Error al guardar los cambios. Por favor, inténtalo de nuevo.');
    }
  };
  // Cancel edit
  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setEditingItem(null);
  };

  // Handle create plan
  const handleCreatePlan = async (newItem: Partial<DiaryItem>) => {
    if (!id) return;
    
    try {
      // Call the API to create the diary item
      await diaryService.createDiaryItem(id, newItem);
      
      // Refetch all diary items to get the latest data from the server
      const diaryItems = await diaryService.getAllDiaryItemsByUserId(id);
      const sortedItems = diaryItems.sort((a, b) => (a.timeOfDay || '').localeCompare(b.timeOfDay || ''));
      setPlanItems(sortedItems);
      
      setCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating diary item:', error);
      alert('Error al crear el plan. Por favor, inténtalo de nuevo.');
    }
  };

  // Cancel create
  const handleCancelCreate = () => {
    setCreateModalOpen(false);
  };

  // Handle patient update
  const handleUpdatePatient = async (data: Partial<UpdatePatientData>) => {
    if (!id) return;
    
    try {
      await patientsService.updatePatient(id, data);
      setEditPatientModalOpen(false);
      // Refetch patient data from API
      const patientData = await patientsService.getPatientById(id);
      setPatient(patientData);
    } catch (error) {
      console.error('Failed to update patient:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  // Handle patient deactivation
  const handleDeletePatient = async () => {
    if (!patient?.userId) return;
    
    try {
      await patientsService.deactivatePatient(patient.userId);
      setDeleteConfirmOpen(false);
      // Navigate back to the patients list after successful deactivation
      navigate('/app');
    } catch (error) {
      console.error('Failed to deactivate patient:', error);
      alert('Error al desactivar el paciente. Por favor, inténtalo de nuevo.');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app/patients')} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Cargando datos del paciente...</div>
        </div>
      </div>
    );
  }


  if (!patient) {
    return (
      <div style={{ padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app/patients')} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Paciente no encontrado.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app/patients')} />
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 40, marginBottom: 32 }}>
          {/* Left: Patient Info */}
          <Section style={{ minWidth: 320, maxWidth: 360, flex: '0 0 340px', marginBottom: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              {/* Avatar */}
              <div style={{ width: 100, height: 100, borderRadius: '50%', overflow: 'hidden', position: 'relative' }}>
                {patient.avatarUrl ? (
                  <img 
                    src={patient.avatarUrl} 
                    alt={`${patient.firstName} ${patient.lastName}`}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    background: '#38b2ac', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: 40, 
                    fontWeight: 700, 
                    color: '#fff' 
                  }}>
                    {(patient.firstName || '').charAt(0).toUpperCase()}{(patient.lastName || '').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div style={{ fontWeight: 700, fontSize: 22, color: '#222', textAlign: 'center' }}>
                {patient.firstName || ''} {patient.lastName || ''}
              </div>
              <div style={{ color: '#6b7280', fontSize: 14, textAlign: 'center' }}>
                @{patient.username || ''}
              </div>
              <button 
                onClick={() => setEditPatientModalOpen(true)} 
                style={{
                  marginTop: 8,
                  background: '#38b2ac',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 24px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#319795';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#38b2ac';
                }}
              >
                Editar Datos
              </button>
            </div>
            
            {/* Inactivity Alert */}
            {(() => {
              const status = getPatientActivityStatus(patient);
              if (status === 'never') return (
                <div style={{
                  marginTop: 20,
                  background: '#fef2f2',
                  border: '1px solid #fca5a5',
                  borderRadius: 8,
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}>
                  <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>🚫</span>
                  <div style={{ fontSize: 13, color: '#991b1b', lineHeight: 1.4 }}>
                    <strong>Nunca ha accedido a la app</strong>
                    <div style={{ marginTop: 4 }}>
                      Este paciente no ha utilizado la aplicación.
                    </div>
                  </div>
                </div>
              );
              if (status === 'inactive') return (
                <div style={{
                  marginTop: 20,
                  background: '#fffbeb',
                  border: '1px solid #fbbf24',
                  borderRadius: 8,
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}>
                  <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>⚠️</span>
                  <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.4 }}>
                    <strong>Sin actividad reciente</strong>
                    <div style={{ marginTop: 4 }}>
                      Última actividad: {new Date(patient.lastActivityAt!).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                </div>
              );
              return null;
            })()}

            {/* Patient Info Display */}
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Email</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.email || '-'}</div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Teléfono</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.phone_number || '-'}</div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Género</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>
                  {patient.gender === 'male' ? 'Masculino' : patient.gender === 'female' ? 'Femenino' : patient.gender === 'other' ? 'Otro' : '-'}
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Dirección</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.address || '-'}</div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Fecha de Nacimiento</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>
                  {patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString('es-ES') : '-'}
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Términos y Condiciones Aceptados</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>
                  {patient.termsAndConditionsAccepted ? new Date(patient.termsAndConditionsAccepted).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Última Actividad</div>
                <div style={{ fontSize: 15, color: '#1f2937' }}>
                  {patient.lastActivityAt ? new Date(patient.lastActivityAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Notificaciones Push</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 12, backgroundColor: patient.fcm_token ? '#dcfce7' : '#f3f4f6', color: patient.fcm_token ? '#16a34a' : '#6b7280' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: patient.fcm_token ? '#16a34a' : '#9ca3af', display: 'inline-block' }} />
                  {patient.fcm_token ? 'Activadas' : 'No activadas'}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>RMR</div>
                  <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.rmrKcalDay || '-'} {patient.rmrKcalDay ? 'kcal/día' : ''}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>Peso</div>
                  <div style={{ fontSize: 15, color: '#1f2937' }}>{patient.weight || '-'} {patient.weight ? 'kg' : ''}</div>
                </div>
              </div>
              
              <button 
                onClick={() => setDeleteConfirmOpen(true)} 
                style={{
                  marginTop: 24,
                  width: '100%',
                  background: 'transparent',
                  color: '#e53e3e',
                  border: '1px solid #e53e3e',
                  borderRadius: 6,
                  padding: '10px 24px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fff5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Eliminar Usuario
              </button>
            </div>
          </Section>
          {/* Right: Plan, Tabs, Graphs */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tabs tabs={["Estadísticas", "Plan", "Documentos", "Recordatorios"]} selected={selectedTab} onSelect={setSelectedTab} />
            {selectedTab === 0 && (
              <>
                <Section>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: '#222' }}>Grafica Adherencia al plan</div>
                  <AdherenceChart 
                    data={adherenceData}
                    loading={adherenceLoading}
                    error={adherenceError}
                  />
                </Section>
                <Section>
                  <div style={{ fontWeight: 700, marginBottom: 12, color: '#222' }}>Calendario de Adherencia</div>
                  <AdherenceHeatmap
                    data={heatmapData}
                    loading={heatmapLoading}
                    error={heatmapError}
                  />
                </Section>
              </>
            )}
            {selectedTab === 1 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16, gap: 12 }}>
                  <WeekTabs selected={selectedDay} onSelect={setSelectedDay} />
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={() => {
                        setCreateModalItemType('FitnessItem');
                        setCreateModalOpen(true);
                      }}
                      style={{
                        background: '#38b2ac',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '8px 16px',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#319795';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#38b2ac';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: 16 }}>🏃</span>
                      <span>Actividad</span>
                    </button>
                    <button
                      onClick={() => {
                        setCreateModalItemType('SupplementItem');
                        setCreateModalOpen(true);
                      }}
                      style={{
                        background: '#8b5cf6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '8px 16px',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#7c3aed';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#8b5cf6';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: 16 }}>💊</span>
                      <span>Suplemento</span>
                    </button>
                  </div>
                </div>
                {diaryLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    Cargando plan...
                  </div>
                ) : diaryError ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ color: '#e53e3e', marginBottom: 16 }}>{diaryError}</div>
                  </div>
                ) : filteredPlanItems.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    No hay actividades planificadas para {selectedDayName}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filteredPlanItems.map((item, index) => (
                      <div 
                        key={`${item.id}-${item.dayOfWeek}-${index}`} 
                        style={{ position: 'relative' }}
                      >
                        <DiaryItemCard item={item} />
                        <button
                          onClick={() => handleEditItem(item)}
                          style={{ position: 'absolute', top: 10, right: 10, background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                        >Editar</button>
                      </div>
                    ))}
                  </div>
                )}
                <DiaryItemCreateModal
                  open={editModalOpen}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                  editingItem={editingItem}
                  mode="edit"
                  patientUserId={patient?.userId}
                />
                <DiaryItemCreateModal
                  open={createModalOpen}
                  onSave={handleCreatePlan}
                  onCancel={handleCancelCreate}
                  defaultDayOfWeek={selectedDayName}
                  defaultItemType={createModalItemType}
                  patientUserId={patient?.userId}
                />
              </Section>
            )}
            {selectedTab === 2 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <DocumentManager patientId={id || ''} />
              </Section>
            )}
            {selectedTab === 3 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#222' }}>Recordatorios del paciente</div>
                  <button
                    onClick={() => setCreateReminderModalOpen(true)}
                    style={{
                      background: '#38b2ac',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 16px',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                    <span>Añadir Recordatorio</span>
                  </button>
                </div>
                {remindersLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    Cargando recordatorios...
                  </div>
                ) : remindersError ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#e53e3e' }}>
                    {remindersError}
                  </div>
                ) : reminders.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    No hay recordatorios configurados para este paciente.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {reminders.map((reminder) => (
                      <ReminderCard
                        key={reminder.id}
                        reminder={reminder}
                        patientId={id || ''}
                        onToggleEnabled={handleToggleReminderEnabled}
                        onEdit={(r) => { setEditingReminder(r); setEditReminderModalOpen(true); }}
                        onDelete={handleDeleteReminder}
                      />
                    ))}
                  </div>
                )}
                <ReminderModal
                  open={createReminderModalOpen}
                  mode="create"
                  onSave={handleCreateReminder}
                  onCancel={() => setCreateReminderModalOpen(false)}
                />
                <ReminderModal
                  open={editReminderModalOpen}
                  mode="edit"
                  reminder={editingReminder}
                  onSave={handleUpdateReminder}
                  onCancel={() => { setEditReminderModalOpen(false); setEditingReminder(null); }}
                />
              </Section>
            )}
          </div>
        </div>
      </div>

      {/* Edit Patient Modal */}
      <EditPatientModal
        open={editPatientModalOpen}
        patient={patient}
        onSave={handleUpdatePatient}
        onCancel={() => setEditPatientModalOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Confirmar Eliminación"
        message={
          <>
            ¿Estás seguro de que deseas desactivar a <strong>{patient?.firstName} {patient?.lastName}</strong>? 
            Esta acción desactivará el acceso del paciente al sistema.
          </>
        }
        confirmText="Sí, Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeletePatient}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
};

export default PatientDetail; 